import React, { useEffect, useState, useRef } from 'react'
import { useSelector } from 'react-redux'

import { Select } from 'antd'
import styled from 'styled-components'
import { colours } from '@qflow/theme'

import {
  projectConnectionsSummary,
  ProjectConnectionsSummaryTable
} from 'portal/components/ProjectConnectionsSummary'
import { ColourButton, icons, Spacer } from 'portal/lib/primitives'
import { Flag } from 'portal/lib/primitives/icons/Flag'
import { useBeamerBarHeight } from 'portal/lib/hooks'
import { accountProjectsGet } from 'portal/store/allAccountProjects/actions'
import { showUnsavedChangesModal } from 'portal/utils/unsavedChanges'

import { AlertSettings, ProjectSettings } from './AlertSettings'
import { Files } from './Files'
import { Integrations, ProjectIntergrationsSettings } from './Integrations'
import { Tags } from './Tags'
import { WmrConfig } from './WmrConfig'

export interface accountProjects {
  name: string
  projectId: string
  country?: string
}

export const ProjectSelector = ({
  dataView,
  selectedProjectId,
  setSelectedProjectId,
  onHasUnsavedChanges
}) => {
  const [useFlags, setUseFlags] = useState(false)
  // Base height for page header, title, buttons, padding, plus the BeamerBar if present
  const maxHeight = `calc(100vh - ${242 + useBeamerBarHeight()}px)`
  const hasAlertUnsavedChanges = useRef(false)
  const pendingProjectId = useRef<string | null>(null)

  const accountProjects = useSelector<
    any,
    {
      error: any
      loading: boolean
      projects: accountProjects[]
    }
  >(state => state.accountProjects)

  const accountSummary = useSelector<
    any,
    {
      error: any
      loading: boolean
      accountConnectionsSummary: projectConnectionsSummary[]
    }
  >(state => state.accountConnectionsSummary)

  useEffect(() => {
    if (
      !selectedProjectId &&
      accountProjects.projects &&
      accountProjects.projects.length > 0
    ) {
      // Filter for UK projects if dataView is wmrConfig
      const projectsToShow =
        dataView === 'wmrConfig'
          ? accountProjects.projects.filter(p => p.country === 'United Kingdom')
          : accountProjects.projects

      if (projectsToShow.length > 0) {
        setSelectedProjectId(projectsToShow[0].projectId)
      }
    }
  }, [
    accountProjects.projects,
    selectedProjectId,
    setSelectedProjectId,
    dataView
  ])

  useEffect(() => {
    // Loop through pre-filtered projects to see if more than 1 country exists, if so, enable useFlags
    const countrySet = new Set()
    accountProjects.projects?.forEach(project => {
      if (project.country) {
        countrySet.add(project.country)
      }
    })
    if (countrySet.size > 1) {
      setUseFlags(true)
    } else {
      setUseFlags(false)
    }
  }, [accountProjects.projects])

  // Filter projects for wmrConfig view to only show UK projects
  const filteredProjects =
    dataView === 'wmrConfig'
      ? accountProjects.projects?.filter(p => p.country === 'United Kingdom')
      : accountProjects.projects

  const accountProjectsForSelect = filteredProjects?.map(m => ({
    value: m.projectId,
    label: m.name,
    country: m.country
  }))

  const handleProjectChange = (projectId: string) => {
    if (dataView === 'alertSettings' && hasAlertUnsavedChanges.current) {
      pendingProjectId.current = projectId
      showUnsavedChangesModal({
        onConfirm: () => {
          hasAlertUnsavedChanges.current = false
          setSelectedProjectId(projectId)
          pendingProjectId.current = null
        },
        onCancel: () => {
          pendingProjectId.current = null
        }
      })
    } else {
      setSelectedProjectId(projectId)
    }
  }

  const handleAlertUnsavedChanges = (hasChanges: boolean) => {
    hasAlertUnsavedChanges.current = hasChanges
    // Also notify parent component if callback is provided
    if (onHasUnsavedChanges) {
      onHasUnsavedChanges(hasChanges)
    }
  }

  return (
    <Container>
      <ProjectsList $maxHeight={maxHeight}>
        <h4>Projects</h4>
        {(accountProjects.loading || !accountProjects.projects) && (
          <div>Loading projects...</div>
        )}
        {!accountProjects.loading && accountProjects.error && (
          <div>Error loading projects!</div>
        )}
        {!accountProjects.loading &&
          !accountProjects.error &&
          filteredProjects && (
            <>
              <Select
                allowClear
                showSearch
                suffixIcon={<icons.MagnifyingGlass colour={'currentColor'} />}
                optionFilterProp="label"
                style={{ width: '100%', marginBottom: '1rem' }}
                options={accountProjectsForSelect}
                placeholder="Search projects..."
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? '')
                    .toLowerCase()
                    .localeCompare((optionB?.label ?? '').toLowerCase())
                }
                onSelect={value => {
                  handleProjectChange(value as string)
                }}
              />
              {filteredProjects.map(m => (
                <ProjectButton
                  onClick={() => {
                    handleProjectChange(m.projectId)
                  }}
                  key={m.projectId}
                  className={selectedProjectId === m.projectId ? 'active' : ''}
                >
                  {useFlags && m.country ? (
                    <Flag
                      country={m.country}
                      style={{
                        marginTop: '6px',
                        marginRight: '0.25rem'
                      }}
                    />
                  ) : (
                    <icons.Table
                      colour="currentColor"
                      style={{ marginTop: '0.25rem' }}
                    />
                  )}
                  <Spacer.Fine />
                  <ProjectName>{m.name}</ProjectName>
                </ProjectButton>
              ))}
            </>
          )}
      </ProjectsList>
      {!accountSummary.loading &&
        !accountSummary.error &&
        accountSummary.accountConnectionsSummary &&
        selectedProjectId && (
          <ContentSection $maxHeight={maxHeight}>
            {dataView === 'projectData' && (
              <ProjectConnectionsSummaryTable
                summary={
                  accountSummary.accountConnectionsSummary.find(
                    sum => sum.projectId === selectedProjectId
                  ) as projectConnectionsSummary
                }
              />
            )}
            {dataView === 'alertSettings' && (
              <AlertSettings
                project={
                  accountProjects.projects?.find(
                    sum => sum.projectId === selectedProjectId
                  ) as ProjectSettings
                }
                onHasUnsavedChanges={handleAlertUnsavedChanges}
              />
            )}
            {dataView === 'integrations' && (
              <Integrations
                project={
                  accountProjects.projects?.find(
                    sum => sum.projectId === selectedProjectId
                  ) as ProjectIntergrationsSettings
                }
                projectsReloader={accountProjectsGet}
              />
            )}
            {dataView === 'wmrConfig' && (
              <WmrConfig
                project={
                  accountProjects.projects?.find(
                    sum => sum.projectId === selectedProjectId
                  ) as ProjectSettings
                }
              />
            )}
            {dataView === 'files' && (
              <Files
                project={
                  accountProjects.projects?.find(
                    sum => sum.projectId === selectedProjectId
                  ) as ProjectSettings
                }
              />
            )}
            {dataView === 'tags' && (
              // Keyed by project so switching project remounts
              // Tags instead of reusing previous project's state
              <Tags
                key={selectedProjectId}
                project={
                  accountProjects.projects?.find(
                    sum => sum.projectId === selectedProjectId
                  ) as ProjectSettings
                }
              />
            )}
          </ContentSection>
        )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  align-items: top;
  gap: 1rem;
`
const ProjectsList = styled.div<{ $maxHeight: string }>`
  width: 15.9375rem;
  flex-shrink: 0;
  background: ${colours.WHITE};
  border-radius: 0.625rem;
  padding: 0.625rem 1rem;
  box-shadow: 0px 1px 3px 0px #00000026;
  max-height: ${props => props.$maxHeight};
  overflow-y: auto;

  & > h4 {
    color: ${colours.CORE_NAVY};
    font-size: 0.9375rem;
    font-weight: 700;
  }
`
const ContentSection = styled.div<{ $maxHeight: string }>`
  flex: 1;
  max-height: ${props => props.$maxHeight};
  overflow-y: auto;
  padding-bottom: 0.125rem;
`
const ProjectButton = styled(ColourButton.BasicBorderless)`
  font-weight: 500;
  text-align: left;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  max-height: none;
  margin-bottom: 0.125rem;
  border-radius: 5px;

  &:hover,
  &:focus,
  &:active {
    background-color: ${colours.CONTROL_PANEL_GREY};
    border-color: ${colours.CONTROL_PANEL_GREY};
  }

  &.active {
    background-color: ${colours.PALE_TEAL};
  }
`
const ProjectName = styled.div`
  max-width: calc(100% - 1.125rem);
`
